import { useMemo, useState } from 'react';
import { ChevronDown, Link2, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type CampaignOption = {
  play_id: number;
  name: string;
};

type GoalCampaignLinkerProps = {
  playIds: number[];
  plays: CampaignOption[];
  disabled?: boolean;
  busy?: boolean;
  onChange: (playIds: number[]) => void;
  /** Compact trigger for dense table cells. */
  dense?: boolean;
  className?: string;
  'aria-label'?: string;
};

/**
 * Multi-select campaign linker: chips for linked campaigns + checkbox menu.
 * Empty selection = unlinked.
 */
export function GoalCampaignLinker({
  playIds,
  plays,
  disabled,
  busy,
  onChange,
  dense,
  className,
  'aria-label': ariaLabel = 'Link campaigns',
}: GoalCampaignLinkerProps) {
  const [query, setQuery] = useState('');

  const selected = useMemo(() => {
    const byId = new Map(plays.map((p) => [p.play_id, p]));
    return playIds
      .map((id) => byId.get(id))
      .filter((p): p is CampaignOption => p != null);
  }, [playIds, plays]);

  const filteredPlays = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return plays;
    return plays.filter((p) => p.name.toLowerCase().includes(q));
  }, [plays, query]);

  const toggle = (playId: number) => {
    if (playIds.includes(playId)) {
      onChange(playIds.filter((id) => id !== playId));
    } else {
      onChange([...playIds, playId]);
    }
  };

  const remove = (playId: number) => {
    onChange(playIds.filter((id) => id !== playId));
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {selected.length === 0 ? (
          <span
            className={cn(
              'inline-flex items-center rounded-md border border-dashed px-2 py-0.5 text-[11px] font-medium',
              'border-amber-200 bg-amber-50 text-amber-800',
            )}
          >
            Unlinked
          </span>
        ) : (
          selected.map((p) => (
            <span
              key={p.play_id}
              className={cn(
                'inline-flex max-w-[11rem] items-center gap-1 rounded-md border border-gray-200 bg-gray-50 pl-2 text-[11px] font-medium text-gray-800',
                dense ? 'py-0.5 pr-0.5' : 'py-1 pr-1',
              )}
              title={p.name}
            >
              <span className="truncate">{p.name}</span>
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => remove(p.play_id)}
                className="rounded p-0.5 text-gray-400 hover:bg-white hover:text-gray-700 disabled:opacity-50"
                aria-label={`Unlink ${p.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))
        )}

        <DropdownMenu
          onOpenChange={(open) => {
            if (!open) setQuery('');
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={disabled || busy || plays.length === 0}
              aria-label={ariaLabel}
              className={cn(
                'inline-flex h-7 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 text-[11px] font-medium text-gray-700',
                'hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Link2 className="h-3 w-3 text-gray-500" />
              )}
              {selected.length === 0 ? 'Link campaigns' : 'Edit'}
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
            <div className="border-b border-gray-100 p-2">
              <DropdownMenuLabel className="px-1 pb-1.5 pt-0 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Campaigns
              </DropdownMenuLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search campaigns…"
                  className="h-8 w-full rounded-md border border-gray-200 bg-white pl-7 pr-2 text-xs outline-none ring-blue-500 focus:ring-2"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto p-1">
              {filteredPlays.length === 0 ? (
                <div className="px-2 py-6 text-center text-xs text-gray-500">No campaigns match</div>
              ) : (
                filteredPlays.map((p) => {
                  const checked = playIds.includes(p.play_id);
                  return (
                    <DropdownMenuCheckboxItem
                      key={p.play_id}
                      checked={checked}
                      onCheckedChange={() => toggle(p.play_id)}
                      onSelect={(e) => e.preventDefault()}
                      className="text-xs"
                    >
                      <span className="truncate">{p.name}</span>
                    </DropdownMenuCheckboxItem>
                  );
                })
              )}
            </div>
            {playIds.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-xs text-amber-800 focus:text-amber-900"
                  onSelect={() => onChange([])}
                >
                  Clear all links
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {selected.length > 1 && (
        <p className="text-[10px] text-gray-500">
          Linked to {selected.length} campaigns
        </p>
      )}
    </div>
  );
}
